import type { PostHog } from 'posthog-js';
import { createRandomId } from 'utils/createRandomId';
import { safeLocalStorage, safeSessionStorage } from 'utils/safeStorage';

const SESSION_DISTINCT_ID_KEY = 'bizly_session_distinct_id';

/** In-memory fallback when sessionStorage is unavailable — keeps bootstrap and beacons aligned. */
let memoryDistinctId: string | null = null;

const getPostHogPersistenceKey = (): string | undefined => {
    const token = import.meta.env.VITE_APP_PUBLIC_POSTHOG_KEY;
    return token ? `ph_${token}_posthog` : undefined;
};

/** Read PostHog's persisted anonymous id so beacon events match the SDK once it loads. */
export const getPersistedPosthogDistinctId = (): string | undefined => {
    try {
        const key = getPostHogPersistenceKey();
        if (!key) {
            return undefined;
        }

        const raw = safeLocalStorage.get(key);
        if (!raw) {
            return undefined;
        }

        const parsed = JSON.parse(raw) as { distinct_id?: string };
        return typeof parsed.distinct_id === 'string' ? parsed.distinct_id : undefined;
    } catch {
        return undefined;
    }
};

/** Session-scoped id used before PostHog initializes; passed to SDK via bootstrap. */
export const getOrCreateSessionDistinctId = (): string => {
    const persisted = getPersistedPosthogDistinctId();
    if (persisted) {
        return persisted;
    }

    if (memoryDistinctId) {
        return memoryDistinctId;
    }

    const session = safeSessionStorage.get(SESSION_DISTINCT_ID_KEY);
    if (session) {
        memoryDistinctId = session;
        return session;
    }

    const id = createRandomId();
    memoryDistinctId = id;

    // Best effort; memoryDistinctId keeps subsequent calls stable if the write is rejected.
    safeSessionStorage.set(SESSION_DISTINCT_ID_KEY, id);

    return id;
};

export const isPostHogReady = (client?: PostHog | null): client is PostHog => Boolean(client?.__loaded);

/** Distinct id for analytics capture — prefers initialized SDK, then PostHog storage, then session id. */
export const getAnalyticsDistinctId = (posthog?: PostHog | null): string => {
    if (isPostHogReady(posthog)) {
        const id = posthog.get_distinct_id();
        if (id) {
            return id;
        }
    }

    return getOrCreateSessionDistinctId();
};
